import { FJWASMLoader } from '@fj/codec';
import FJConfig from '../config/FJConfig.ts';
import FJConstant from '../constants/FJConstant.ts';
import FJCoreConfig from './FJCoreConfig.js';
import FJVideoPlayBase from './FJVideoPlayBase.js';
import { renderThumbnails } from '../store/FJVideoThumbnailStore.js';
import FJPoolStore from '../store/FJPoolStore.js';
import FJArchiveUtil from '../util/FJArchiveUtil.js';
import FJBlurUtil from '../util/FJBlurUtil.js';
import FJMathUtil from '../util/FJMathUtil.js';
import FJFaceUtil from '../util/FJFaceUtil.js';
import { getPersonCropRectFromFaceData } from '../lib/faceDetection/personCropFromFace.js';

/** 无显式输出宽高时，`getFaceTimelineRenderRects` 按输出比例推算尺寸所用的长边像素 */
const TIMELINE_OUTPUT_MAX_EDGE = 1080;

/** 画面模式切换：交叉淡化（上一幅淡出与下一幅淡入全程重叠），总时长（毫秒），预览与导出共用 */
const PREVIEW_MODE_TRANSITION_MS = 100; // ms

/** 交叉淡化进度缓动：CSS cubic-bezier(x1,y1,x2,y2)，经 FJMathUtil.cubicBezierEase 映射 */
const TRANSITION_CROSSFADE_CUBIC_BEZIER = Object.freeze({
    x1: 0.42,
    y1: 0,
    x2: 0.58,
    y2: 1,
});

/**
 * @param {number|string|{ width: number, height: number }|null|undefined} ratio 输出宽高比（宽/高），如 `9/16`、`"9:16"`、`{width:9,height:16}`
 * @returns {number | null}
 */
function parseOutputAspectRatio(ratio) {
    if (ratio == null) return null;
    if (typeof ratio === 'number' && Number.isFinite(ratio) && ratio > 0) {
        return ratio;
    }
    if (typeof ratio === 'string') {
        const parts = ratio.split(':').map(s => parseFloat(s.trim()));
        if (parts.length === 2 && parts.every(n => Number.isFinite(n) && n > 0)) {
            return parts[0] / parts[1];
        }
    }
    if (ratio && typeof ratio === 'object' && ratio.width > 0 && ratio.height > 0) {
        return ratio.width / ratio.height;
    }
    return null;
}

/** 相邻帧皆为 `single_cover` / `subject_cover` 单层时：原片裁切矩形（归一化 0～1）各分量偏差均 ≤ 此值则认为「构图未变」，不武装叠化转场 */
const SINGLE_COVER_NO_TRANSITION_RECT_MAX_DELTA = 0.1;

const PRIMARY_COVER_LAYER_ROLES = ['single_cover', 'subject_cover'];

/** @param {{ role: string, sourceCrop?: object }[]} [layers] */
function extractSingleCoverSourceCrop01(layers) {
    if (!Array.isArray(layers)) {
        return null;
    }
    const L = layers.find(l => l && PRIMARY_COVER_LAYER_ROLES.includes(l.role) && l.sourceCrop);
    const r = L && L.sourceCrop;
    if (!r) return null;
    const { x, y, width, height } = r;
    if ([x, y, width, height].some(v => typeof v !== 'number' || !Number.isFinite(v))) {
        return null;
    }
    return { x, y, width, height };
}

/** @returns {boolean} 两矩形在 [-1,1] 画布归一坐标下分量最大差均在 `maxDelta` 内 */
function normRectsAllComponentsCloseEnough(a, b, maxDelta) {
    return (
        Math.abs(a.x - b.x) <= maxDelta &&
        Math.abs(a.y - b.y) <= maxDelta &&
        Math.abs(a.width - b.width) <= maxDelta &&
        Math.abs(a.height - b.height) <= maxDelta
    );
}

function createRenderLane() {
    return {
        lastProfile: null,
        lastTrackSignature: null,
        lastSingleCoverSourceCrop: null,
        lastW: 0,
        lastH: 0,
        wallStartMs: 0,
        timelineStartSec: null,
        snapshot: null,
        compose: null,
        lastOut: null,
    };
}

class FJVideoElement {
    constructor() {
        this._id = FJArchiveUtil.createId(true);
        this._viewCanvas = null;
        this._viewCtx = null;
        this._rect = {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
        };
        this._originalSize = {
            width: 0,
            height: 0,
        };
        this._videoPlayer = null;
        /** `true` 时与 `FJProjectStore._sharedVideoPlayBase` 同源，`clear()` 不调用 `FJVideoPlayBase.clearAll` */
        this._usesSharedVideoPlayBase = false;
        this._videoDuration = 0;
        this._isLightVideo = false;
        // 视频开始时长
        this._startTime = 0;
        // 视频结束时长
        this._endTime = 0;
        this._isPlaying = false;
        // 时间线播放时长 视频是的当前时长 - 视频开始时长
        this._timeLineCurrentTime = 0;
        this._backgroundBlurData = {
            blurType: 'GaussianBlurShader',
            intensity: 40,
        };

        /** 预览 / 导出各自一套转场与上一帧缓存，避免同时播放与编码时互相覆盖 */
        this._renderLanePreview = createRenderLane();
        this._renderLaneExport = createRenderLane();
        /** 模糊前暂存图源：预览与导出分槽，避免交错 drawImage 互相踩 */
        this._blurOriginByLane = { preview: null, export: null };

        /** 人脸时间线；由 setFaceDetectionData / 分析管线写入 */
        this._faceDetectionData = [];
        /** 裁剪终点触发的「先补完转场再 pause」异步流程，防重复入队 */
        this._playbackEndFlushPending = false;
        /** 缓冲态回调；`init` 重建 `_videoPlayer` 后会再次挂载 */
        this._bufferingListener = null;

        /**
         * 16:9→9:16 单人小脸分屏：下半人物取景相对默认框的同比缩放。**越小则取景越紧、人脸越大**（建议约 0.35～0.85）。
         * @see setSingle169TinySplitOptions
         */
        this._single169TinySplitBottomCropScale = FJFaceUtil.SINGLE_169_BOTTOM_PERSON_ZOOM;

        /**
         * 详情页/导出目标比例（如 `FJDetailManager.init` 的 `exportRatio`），用于预计算时间线构图缓存；与预览画布像素无关，仅定输出宽高比。
         */
        this._previewExportRatio = null;

        /**
         * 与 `drawVideoFrameAtTimeToCanvas` 同源：`blocks` 为 **[_startTime, _endTime]** 的连续划分（无间隙、无重叠），每段存该片内用于绘制的 `layers` / `profile` / `trackSignature`（在子区间中点采样）；与人脸块是否为空无关。
         * @type {{ valid: boolean, outputAr: number, canvasW: number, canvasH: number, fw: number, fh: number, bottomCropScale: number, clipStart: number, clipEnd: number, blocks: Array<{ startTime: number, endTime: number, layers: object[], segments: object[], profile: string, trackSignature: string }> }}
         */
        this._timelineRenderCache = {
            valid: false, // 当前缓存是否与最近一次重建参数一致、可直接用于绘制
            outputAr: 0, // 构建缓存时输出画布宽高比 cw/ch
            canvasW: 0, // 构建缓存所用的逻辑输出宽（像素）
            canvasH: 0, // 构建缓存所用的逻辑输出高（像素）
            fw: 0, // 构建缓存时片源宽（与 _originalSize 一致）
            fh: 0, // 构建缓存时片源高
            bottomCropScale: 0, // 构建缓存时单人 169 小脸下半取景缩放
            clipStart: 0, // 与 _startTime 同步，裁剪起点（片源秒）
            clipEnd: 0, // 与 _endTime 同步，裁剪终点（片源秒）
            blocks: [], // 时间轴连续子区间列表，每段含 layers / profile 等
        };

        /** 与 `getFaceTimelineRenderRects` 预览构建一致的双人竖拼 options（引用保持默认即可） */
        this._timelinePreviewDualSplitOptions = null;
    }

    /**
     * @param {{ bottomCropScale?: number }} [opts]
     */
    setSingle169TinySplitOptions(opts = {}) {
        if (opts.bottomCropScale != null && Number.isFinite(Number(opts.bottomCropScale))) {
            this._single169TinySplitBottomCropScale = FJFaceUtil.clampSingle169BottomCropScale(opts.bottomCropScale);
        }
        this._syncTimelineRenderCacheAfterFaceOrTimeChange();
    }

    /** @returns {{ bottomCropScale: number }} */
    getSingle169TinySplitOptions() {
        return { bottomCropScale: this._single169TinySplitBottomCropScale };
    }

    /** @returns {unknown[]} 人脸检测时间线块（含 startTime/endTime/segments） */
    getFaceDetectionData() {
        return this._faceDetectionData;
    }

    /**
     * 写入人脸时间线并重建 `_timelineRenderCache`（与 `drawVideoFrameAtTimeToCanvas` 使用同一套构图数据）。
     * @param {unknown} data mergeFaceDetectionTimeline 等产出的块数组；非数组则置为 []
     */
    setFaceDetectionData(data) {
        this._faceDetectionData = Array.isArray(data) ? data : [];
        this._syncTimelineRenderCacheAfterFaceOrTimeChange();
    }

    /**
     * 设置预览时间线构图所用的目标宽高比（与 `getFaceTimelineRenderRects` / 内部缓存一致），通常传入 `FJDetailManager.init` 的 `exportRatio`。
     * @param {number|string|{width:number,height:number}|null|undefined} ratio
     */
    setPreviewExportRatio(ratio) {
        this._previewExportRatio = ratio ?? null;
        this._syncTimelineRenderCacheAfterFaceOrTimeChange();
    }

    /** @returns {typeof this._previewExportRatio} */
    getPreviewExportRatio() {
        return this._previewExportRatio;
    }
    getVideoDuration() {
        return this._videoDuration;
    }
    /** @returns {string} 与 `FJVideoPlayBase.getSourceStatus` / `FJConstant` 一致 */
    getSourceStatus() {
        if (!this._videoPlayer) return FJConstant.DEFAULT;
        return this._videoPlayer.getSourceStatus();
    }
    getTimeData() {
        return {
            startTime: this._startTime,
            endTime: this._endTime,
        };
    }
    /**
     * @param {string} url 片源地址；与 `sharedVideoPlayBase` 同时传入时需已在 Store 中对同一 URL `setSource` 完成
     * @param {{ sharedVideoPlayBase?: import('./FJVideoPlayBase.js').default }} [options]
     */
    async init(url, options = {}) {
        const shared = options.sharedVideoPlayBase;
        if (shared) {
            this._videoPlayer = shared;
            this._usesSharedVideoPlayBase = true;
            if (shared.getSourceStatus() !== FJConstant.SUCCESS) {
                throw new Error('[FJVideoElement] shared FJVideoPlayBase 未完成加载');
            }
        } else {
            this._usesSharedVideoPlayBase = false;
            this._videoPlayer = new FJVideoPlayBase();
            this._videoPlayer.clearAll();
            await this._videoPlayer.setSource(url);
        }
        /** 竖屏内联播放；带声播放依赖 `primePlaybackForGesture()` 在手势同步阶段先 play（静音），再在 `play()` 内 seek 后解除静音 */
        const el = this._videoPlayer.getSource?.();
        if (el && typeof el === 'object') {
            el.playsInline = true;
            el.setAttribute?.('playsinline', '');
            el.setAttribute?.('webkit-playsinline', '');
        }
        const info = this._videoPlayer.getSourceInfo();
        const duration = info?.duration ?? 0;
        const width = info?.width ?? 0;
        const height = info?.height ?? 0;
        this._videoDuration = duration;
        this._originalSize = {
            width,
            height,
        };
        this._isLightVideo = options.isLightVideo ?? false;
        this.initVideoRect();
        this._videoPlayer.setBufferingListener(this._bufferingListener);
        this._syncTimelineRenderCacheAfterFaceOrTimeChange();
    }
    getIsLightVideo() {
        return this._isLightVideo;
    }

    /**
     * @param {((loading: boolean) => void) | null} fn
     */
    setBufferingListener(fn) {
        this._bufferingListener = typeof fn === 'function' ? fn : null;
        if (this._videoPlayer) {
            this._videoPlayer.setBufferingListener(this._bufferingListener);
        }
    }

    /**
     * 须在用户点击播放的**同步调用栈**内执行：`muted + play()` 占用手势，后续才可安全 `await seek` 再解除静音。
     * （若在异步链路之后才调用带声音的 play()，浏览器常拒绝。）
     */
    primePlaybackForGesture() {
        const vp = this._videoPlayer;
        if (!vp || typeof vp.getSource !== 'function') return;
        const v = vp.getSource();
        if (!v) return;
        v.playsInline = true;
        v.setAttribute?.('playsinline', '');
        v.setAttribute?.('webkit-playsinline', '');
        v.muted = true;
        void v.play().catch(() => {});
    }

    // -------------------------------------------------------------------------
    // 标识与源信息
    // -------------------------------------------------------------------------
    getId() {
        return this._id;
    }
    async getSourceBlob() {
        return await this._videoPlayer.getSourceBlob();
    }

    getSourceUrl() {
        return this._videoPlayer.getSourceUrl();
    }

    getSourceInfo() {
        return this._videoPlayer.getSourceInfo();
    }

    // -------------------------------------------------------------------------
    // 缩略图
    // -------------------------------------------------------------------------

    /** 按目标条高度与片源比例，计算横向拼接的单块缩略尺寸 */
    _thumbnailTileSize(thumbnailSize, sourceSize) {
        return {
            width: Math.floor(thumbnailSize.height * (sourceSize.width / sourceSize.height)),
            height: thumbnailSize.height,
        };
    }

    /**
     * @desc 更新缩略图 并返回缩略图链接
     * @returns {Promise<string>}
     */
    async updateThumbnailBlobUrl(thumbnailSize) {
        return await this._videoUpdateThumbnailBlobUrl(thumbnailSize);
    }

    async _imageBitmapToBlob(imageBitmap) {
        const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height);
        const context = canvas.getContext('2d');
        context.drawImage(imageBitmap, 0, 0);
        return canvas.convertToBlob();
    }

    async getThumbnailBlobUrlByFirstFrame(thumbnailSize, callback) {
        const sourceUrl = this._videoPlayer.getSourceUrl();
        if (!sourceUrl) {
            console.log('no source url');
            return;
        }

        const video = document.createElement('video');
        video.src = sourceUrl;

        const offscreenCanvas = new OffscreenCanvas(thumbnailSize.width, thumbnailSize.height);

        const __onCanplay = () => {
            video.currentTime = 0;
            video.removeEventListener('canplay', __onCanplay);
        };

        const __onSeek = () => {
            video.removeEventListener('seeked', __onSeek);
            const sourceSize = this._videoPlayer.getSize();
            const thumbnailItemSize = this._thumbnailTileSize(thumbnailSize, sourceSize);

            const ctx = offscreenCanvas.getContext('2d');
            const num = Math.ceil(thumbnailSize.width / thumbnailItemSize.width);
            for (let i = 0; i < num; i++) {
                const x = i * thumbnailItemSize.width;
                const y = 0;
                ctx.drawImage(video, x, y, thumbnailItemSize.width, thumbnailItemSize.height);
            }

            offscreenCanvas.convertToBlob().then(blob => {
                const blobUrl = URL.createObjectURL(blob);
                callback && callback(blobUrl);
            });
        };

        video.addEventListener('canplay', __onCanplay);

        video.addEventListener('seeked', __onSeek);
    }

    async _videoUpdateThumbnailBlobUrl(thumbnailSize) {
        const sourceBlob = await this._videoPlayer.getSourceBlob();

        if (!sourceBlob) {
            console.log('no source blob');
            return;
        }
        const sourceSize = this._videoPlayer.getSize();
        const thumbnailItemSize = this._thumbnailTileSize(thumbnailSize, sourceSize);

        let worker = new Worker(FJConfig.VIDEO_THUMBNAIL_WORKER_PATH);
        const readyPromise = new Promise((resolve, reject) => {
            worker.onmessage = e => {
                const msg = e.data;

                if (msg.type === 'ready') {
                    resolve();
                } else if (msg.type === 'error') {
                    console.error(msg.message);
                    reject(new Error(msg.message));
                } else if (msg.type === 'done') {
                    // const ctx = this._videoThumbnailCanvas.getContext('2d');
                    // ctx.drawImage(msg.imageBitMap, 0, 0);
                    // worker.terminate();
                } else {
                    reject(new Error('Not Ready'));
                }
            };
        });

        await readyPromise;
        const wasm = await new Promise(resolve => FJWASMLoader.loadWasmBinary(resolve));

        const offscreenCanvas = new OffscreenCanvas(thumbnailSize.width, thumbnailSize.height);

        const file = new File([sourceBlob], 'video.mp4', { type: 'video/mp4' });
        worker.postMessage(
            {
                wasm,
                offscreenCanvas,
                thumbnailSize: thumbnailItemSize,
                file: file,
                renderInfo: {
                    rotation: 0,
                    flipX: 1,
                    flipY: 1,
                    relativeRect: {
                        x: 0,
                        y: 0,
                        width: 1,
                        height: 1,
                    },
                },
                // 使用获取到的视频信息
                fileInfo: { width: sourceSize.width, height: sourceSize.height },
                start: this._startTime,
                end: this._endTime,
                type: 'RENDER_TUNMBNAILS',
                wasmResourceVersion: FJConfig.CODEC_VERSION,
            },
            [offscreenCanvas],
        );

        try {
            return await new Promise((resolve, reject) => {
                worker.onmessage = async e => {
                    const msg = e.data;
                    if (msg.type === 'done') {
                        const blob = await this._imageBitmapToBlob(msg.imageBitMap);
                        const blobUrl = URL.createObjectURL(blob);

                        worker.terminate();
                        msg.imageBitMap?.close(); // 安全关闭
                        resolve(blobUrl);
                    } else if (msg.type === 'error') {
                        // 增加错误处理细节
                        worker.terminate();
                        reject(new Error(msg.message));
                    }
                };
            });
        } catch (e) {
            console.error(e);
            worker.terminate();
            const sourceUrl = this._videoPlayer.getSourceUrl();
            if (!sourceUrl) return;

            if (this._thumbnailAbortController) {
                this._thumbnailAbortController.abort();
            }
            const canvas = FJPoolStore.getCanvas('2d', true);
            canvas.width = thumbnailSize.width;
            canvas.height = thumbnailSize.height;

            await renderThumbnails(sourceUrl, canvas, {
                thumbnailSize: thumbnailItemSize,
                renderInfo: null,
                fileInfo: { width: sourceSize.width, height: sourceSize.height },
                start: this._startTime,
                end: this._endTime,
                setAbortController: abortController => {
                    this._thumbnailAbortController = abortController;
                },
            });

            const blob = await canvas.convertToBlob();
            const blobUrl = URL.createObjectURL(blob);
            FJPoolStore.releaseCanvas(canvas);
            return blobUrl;
        }
    }

    // -------------------------------------------------------------------------
    // 播放、画布尺寸、时间线
    // -------------------------------------------------------------------------

    /**
     * 用户 seek / 跳转到片头时清预览转场状态，避免沿用上一时刻的 lastProfile 与 lastOut，
     * 否则易触发交叉淡化却只有一次 draw（无后续 rAF），画面卡在 snapshot（常为片尾帧），片头人脸裁切也不生效。
     */
    _resetPreviewLaneOnSeek() {
        const lane = this._renderLanePreview;
        lane.wallStartMs = 0;
        lane.timelineStartSec = null;
        lane.lastProfile = null;
        lane.lastTrackSignature = null;
        lane.lastSingleCoverSourceCrop = null;
    }
    getTimelineRenderCache() {
        return this._timelineRenderCache;
    }

    /**
     * @desc seek
     * @param {number} time
     */
    async seek(time) {
        this._resetPreviewLaneOnSeek();

        this._timeLineCurrentTime = time;

        const sourceCurrentTime = this._timeLineCurrentTime + this._startTime;
        const afterSeeked = () => {
            this._drawVideoCanvasDirect();
        };

        await this._videoPlayer.seek(sourceCurrentTime, afterSeeked);
    }

    /**
     * @desc 设置视频画布
     * @param {HTMLCanvasElement} canvas
     */
    setVideoCanvas(canvas) {
        this._viewCanvas = canvas;
        this._viewCtx = canvas.getContext('2d');

        this.initVideoRect();
    }

    /**
     * @desc 暂停
     */
    async pause() {
        if (!this._videoPlayer) return;
        await this._videoPlayer.pause();
        this._isPlaying = false;
        this._playbackEndFlushPending = false;
    }

    /**
     * 仅重置本实例播放态（不触发底层媒体 pause）。
     * 用于共享播放器被其它实例抢占时，避免本实例下次 play 因 `_isPlaying` 残留而早退。
     */
    stopLocalPlaybackState() {
        this._isPlaying = false;
        this._playbackEndFlushPending = false;
    }

    /**
     * 预览墙钟转场（`wallStartMs`）进行中时，在 `pause` 停掉 rAF 之前补画至交叉淡化结束，避免最后一帧停在叠化中间。
     */
    async _finishPreviewTransitionIfNeeded() {
        const lane = this._renderLanePreview;
        if (!lane.wallStartMs) return;

        const maxFrames = 120;

        return new Promise(resolve => {
            let frames = 0;
            const step = () => {
                if (!this._viewCanvas || !this._viewCtx) {
                    resolve();
                    return;
                }
                this.drawVideoCanvas();
                frames++;
                if (!lane.wallStartMs || frames >= maxFrames) {
                    resolve();
                    return;
                }
                requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        });
    }

    /**
     * 播放结束统一收尾：转场补画 → 本层 pause（同步 `_isPlaying`，底层可能已 pause）→ 通知上层。
     */
    async _completePlayback(pauseCallback) {
        await this._finishPreviewTransitionIfNeeded();
        if (this._isPlaying) {
            await this.pause();
        }
        pauseCallback && pauseCallback(this._timeLineCurrentTime);
    }

    /**
     * @desc 播放（先于底层 play 做 seek 对齐）
     */
    async play(callback, pauseCallback) {
        if (this._isPlaying) return;
        await this.seek(this._timeLineCurrentTime);
        const media = this._videoPlayer.getSource?.();
        if (media) {
            media.muted = false;
        }
        this._playbackEndFlushPending = false;
        this._isPlaying = true;

        const __callback = sourceCurrentTime => {
            const clipLen = Math.max(0, this._endTime - this._startTime);
            const raw = sourceCurrentTime - this._startTime;
            this._timeLineCurrentTime = Math.max(0, Math.min(clipLen, raw));

            callback && callback(this._timeLineCurrentTime);
            this.drawVideoCanvas();

            if (sourceCurrentTime >= this._endTime - 1e-3 && this._isPlaying) {
                if (this._playbackEndFlushPending) return;
                this._playbackEndFlushPending = true;
                void (async () => {
                    try {
                        await this._completePlayback(pauseCallback);
                    } finally {
                        this._playbackEndFlushPending = false;
                    }
                })();
            }
        };

        const __pauseCallback = () => {
            void this._completePlayback(pauseCallback);
        };

        try {
            await this._videoPlayer.play(__callback, __pauseCallback);
        } catch (e) {
            this._isPlaying = false;
            console.warn('[FJVideoElement] play', e);
            throw e;
        }
    }

    /**
     * @desc 初始化适配渲染矩形
     */
    initVideoRect() {
        const isLoaded = this._videoPlayer.getSourceStatus() === FJConstant.SUCCESS;

        if (!isLoaded || !this._viewCanvas) return;

        const domSize = this._viewCanvas.getBoundingClientRect();
        const cssW = Math.max(1, domSize.width);
        const cssH = Math.max(1, domSize.height);
        const cfg = FJCoreConfig.VIDEO_PREVIEW_MAX_LONG_EDGE;
        const maxLong = Math.max(32, typeof cfg === 'number' && cfg > 0 ? cfg : 720);

        let bw;
        let bh;
        if (cssW >= cssH) {
            bw = maxLong;
            bh = Math.max(1, Math.round(maxLong * (cssH / cssW)));
        } else {
            bh = maxLong;
            bw = Math.max(1, Math.round(maxLong * (cssW / cssH)));
        }

        this._viewCanvas.width = bw;
        this._viewCanvas.height = bh;

        const sourceSize = this._videoPlayer.getSize();

        this._rect = {
            width: sourceSize.width,
            height: sourceSize.height,
        };

        if (sourceSize.width > bw || sourceSize.height > bh) {
            const scale = Math.min(bw / sourceSize.width, bh / sourceSize.height);
            this._rect.width = sourceSize.width * scale;
            this._rect.height = sourceSize.height * scale;
        }

        this._rect.x = (bw - this._rect.width) / 2;
        this._rect.y = (bh - this._rect.height) / 2;
    }

    getSourceCurrentTime() {
        return this._videoPlayer.getCurrentTime();
    }

    /** @desc 获取时间线当前时间（用于与字幕同步） */
    getTimeLineCurrentTime() {
        return this._timeLineCurrentTime;
    }

    /**
     * 设置裁剪区间（片源绝对秒）：`_startTime`～`_endTime` 决定时间线长度与人脸缓存的划分范围，并触发 `_timelineRenderCache` 重建。
     * @param {number} startTime 裁剪起点
     * @param {number} endTime 裁剪终点
     */
    setTimeData(startTime, endTime) {
        this._startTime = startTime;
        this._endTime = endTime;
        this._timeLineCurrentTime = 0;
        this._syncTimelineRenderCacheAfterFaceOrTimeChange();
        // seek 参数为**时间线相对**秒（0 = 本事件/裁剪段首帧）；勿传片源绝对 startTime，否则会变成 startTime+startTime
        void this.seek(0);
    }

    /**
     * 释放播放器、清空人脸数据并失效时间线渲染缓存。
     */
    clear() {
        if (this._videoPlayer && !this._usesSharedVideoPlayBase) {
            this._videoPlayer.clearAll();
        }
        this._videoPlayer = null;
        this._usesSharedVideoPlayBase = false;
        this._faceDetectionData = [];
        this._playbackEndFlushPending = false;
        this._invalidateTimelineRenderCache();
    }

    // -------------------------------------------------------------------------
    // 预览/导出绘制（人脸几何与采样见 ../util/FJFaceUtil.js）
    // -------------------------------------------------------------------------

    /**
     * 背景模糊 + 清晰视频层（清晰层为等比 contain，完整显示、可留黑边）
     * @param {'preview'|'export'} [renderLaneKey]
     */
    _drawFitVideoWithBackground(canvas, frame, renderLaneKey = 'preview') {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const { w: fw, h: fh } = FJFaceUtil.getFrameBitmapSize(frame);
        if (fw <= 0 || fh <= 0) return;
        if (this._isLightVideo) {
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        } else {
            this._drawBackgroundBlurToView(canvas, frame, renderLaneKey);
        }
        const drawRect = FJFaceUtil.getDrawRect(
            { width: fw, height: fh },
            { width: canvas.width, height: canvas.height },
        );
        ctx.drawImage(
            frame,
            drawRect.sx,
            drawRect.sy,
            drawRect.sw,
            drawRect.sh,
            drawRect.dx,
            drawRect.dy,
            drawRect.drawW,
            drawRect.drawH,
        );
    }

    /**
     * 输入输出比例一致：整帧直接缩放至画布，不做人脸/模糊分支
     */
    _drawDirectSameAspect(ctx, frame, fw, fh, canvasW, canvasH) {
        ctx.drawImage(frame, 0, 0, fw, fh, 0, 0, canvasW, canvasH);
    }

    /**
     * @param {'preview'|'export'} [renderLaneKey]
     */
    _drawBackgroundBlurToView(view, source, renderLaneKey = 'preview') {
        const { width: viewWidth, height: viewHeight } = view;
        const { w: fW, h: fH } = FJFaceUtil.getFrameBitmapSize(source);
        const originWidth = fW > 0 ? fW : this._originalSize.width;
        const originHeight = fH > 0 ? fH : this._originalSize.height;
        const blurData = this._backgroundBlurData;
        const { width: fillWidth, height: fillHeight } = FJMathUtil.fillSize(view, {
            width: originWidth,
            height: originHeight,
        });
        if (blurData) {
            const { blurType, intensity } = blurData;
            let slot = this._blurOriginByLane[renderLaneKey];
            if (!slot) {
                const canvas = document.createElement('canvas');
                slot = { canvas, ctx: canvas.getContext('2d') };
                this._blurOriginByLane[renderLaneKey] = slot;
            }
            const { canvas: originCanvas, ctx: originCtx } = slot;
            originCanvas.width = originWidth;
            originCanvas.height = originHeight;
            originCtx.save();
            originCtx.fillStyle = '#000000';
            originCtx.fillRect(0, 0, originWidth, originHeight);
            originCtx.drawImage(source, 0, 0, originWidth, originHeight);
            originCtx.restore();
            const result = FJBlurUtil.drawBlur(blurType, originCanvas, {
                blurRadius: intensity,
            });
            if (result) {
                const { offscreenCanvas, size } = result;
                const ctx = view.getContext('2d');

                const sx = (size.width * (fillWidth - viewWidth)) / fillWidth / 2;
                const sy = (size.height * (fillHeight - viewHeight)) / fillHeight / 2;
                const sw = (size.width * viewWidth) / fillWidth;
                const sh = (size.height * viewHeight) / fillHeight;

                ctx.drawImage(offscreenCanvas, sx, sy, sw, sh, 0, 0, viewWidth, viewHeight);
            }
        }
    }

    /** @param {{ snapshot: HTMLCanvasElement | null, compose: HTMLCanvasElement | null, lastOut: HTMLCanvasElement | null }} lane */
    _ensureLaneCanvas(lane, field, cw, ch) {
        let c = lane[field];
        if (!c) {
            c = document.createElement('canvas');
            lane[field] = c;
        }
        if (c.width !== cw || c.height !== ch) {
            c.width = cw;
            c.height = ch;
        }
        return c;
    }

    // -------------------------------------------------------------------------
    // 画面模式切换：交叉淡化与主绘制入口
    // -------------------------------------------------------------------------

    /**
     * 交叉淡化：上一幅 α=1→0 与下一幅 α=0→1 同时进行；进度经 cubic-bezier 变速（TRANSITION_CROSSFADE_CUBIC_BEZIER）
     * @param {{ compose: HTMLCanvasElement | null, snapshot: HTMLCanvasElement | null, wallStartMs: number, timelineStartSec: number | null }} lane
     */
    _compositeModeTransitionCrossfade(destCanvas, cw, ch, elapsedMs, lane) {
        const comp = lane.compose;
        const snap = lane.snapshot;
        const vctx = destCanvas.getContext('2d');
        if (!vctx || !comp || comp.width !== cw || comp.height !== ch) {
            return;
        }

        const total = PREVIEW_MODE_TRANSITION_MS;
        if (elapsedMs >= total || !snap || snap.width !== cw || snap.height !== ch) {
            lane.wallStartMs = 0;
            lane.timelineStartSec = null;
            vctx.clearRect(0, 0, cw, ch);
            vctx.drawImage(comp, 0, 0);
            return;
        }

        const linearT = elapsedMs / total;
        const { x1, y1, x2, y2 } = TRANSITION_CROSSFADE_CUBIC_BEZIER;
        const t = FJMathUtil.cubicBezierEase(linearT, x1, y1, x2, y2);

        vctx.clearRect(0, 0, cw, ch);
        vctx.fillStyle = '#000000';
        vctx.fillRect(0, 0, cw, ch);
        vctx.globalAlpha = 1 - t;
        vctx.drawImage(snap, 0, 0);
        vctx.globalAlpha = t;
        vctx.drawImage(comp, 0, 0);
        vctx.globalAlpha = 1;
    }

    /**
     * 仅从当前 `<video>` 纹理绘制到画布（依赖当前解码时刻与 `this._timeLineCurrentTime`）。
     */
    _drawVideoCanvasDirect() {
        if (!this._viewCanvas || !this._viewCtx || !this._videoPlayer) return;
        const source = this._videoPlayer.getSource();
        this.drawVideoFrameAtTimeToCanvas(this._viewCanvas, source, this._timeLineCurrentTime);
    }

    /**
     * @desc 绘制视频画布
     */
    drawVideoCanvas() {
        if (!this._viewCanvas || !this._viewCtx || !this._videoPlayer) return;
        const source = this._videoPlayer.getSource();
        this.drawVideoFrameAtTimeToCanvas(this._viewCanvas, source, this._timeLineCurrentTime);
    }

    /**
     * 截取当前裁剪区间首帧（时间线 0s），复用 `drawVideoFrameAtTimeToCanvas` 的构图与人脸裁切逻辑，导出封面 Blob。
     *
     * @param {{ width?: number, height?: number, mimeType?: string, quality?: number, maxEdge?: number }} [options]
     * @param {number} [options.width] 输出宽；与 height 同时省略时优先用 `_viewCanvas` 尺寸
     * @param {number} [options.height] 输出高
     * @param {string} [options.mimeType='image/jpeg']
     * @param {number} [options.quality=0.92] 仅对 image/jpeg / webp 有效
     * @param {number} [options.maxEdge=720] 无 viewCanvas 且未指定宽高时，按片源比例以该值为长边推算尺寸
     * @returns {Promise<Blob | null>}
     */
    async getCoverThumbnail(options = {}) {
        const { mimeType = 'image/jpeg', quality = 0.92, maxEdge = 200 } = options;

        if (!this._videoPlayer || this._videoPlayer.getSourceStatus() !== FJConstant.SUCCESS) {
            return null;
        }
        const source = this._videoPlayer.getSource();
        if (!source) {
            return null;
        }

        let cw = options.width;
        let ch = options.height;
        if (!cw || !ch) {
            if (this._viewCanvas && this._viewCanvas.width > 0 && this._viewCanvas.height > 0) {
                cw = this._viewCanvas.width;
                ch = this._viewCanvas.height;
            } else {
                const sz = this._videoPlayer.getSize();
                if (!sz || sz.width <= 0 || sz.height <= 0) {
                    return null;
                }
                const { width: sw, height: sh } = sz;
                if (sw >= sh) {
                    cw = maxEdge;
                    ch = Math.max(1, Math.round(maxEdge * (sh / sw)));
                } else {
                    ch = maxEdge;
                    cw = Math.max(1, Math.round(maxEdge * (sw / sh)));
                }
            }
        }

        const captureFrame = async () => {
            await this._videoPlayer.seek(this._startTime, null);

            this._renderLaneExport = createRenderLane();
            this._blurOriginByLane.export = null;

            const canvas = FJPoolStore.getCanvas('2d');
            canvas.width = cw;
            canvas.height = ch;

            try {
                this.drawVideoFrameAtTimeToCanvas(canvas, source, 0, { transitionClock: 'timeline' });
                return await new Promise(resolve => {
                    canvas.toBlob(blob => resolve(blob ?? null), mimeType, quality);
                });
            } catch (e) {
                console.warn('[FJVideoElement] getCoverThumbnail', e);
                return null;
            } finally {
                FJPoolStore.releaseCanvas(canvas);
            }
        };

        return captureFrame();
    }

    /**
     * 片源时刻下是否要做人脸构图，以及用于转场判等的 profile / 轨迹签名。
     *
     * **构图 profile 一览**（与 `FJFaceUtil.resolveFaceRenderProfile` 及同画幅早退配合；缓存里 `profile` 为同画幅时写 `'same'`，否则为下列之一）：
     * - `same`：进/出画幅比一致（`sameAspect`，整段不做人脸特判）；
     * - `169_dual_tiny` / `169_dual_split`：16:9↔9:16 且双人，小脸合景 vs 上下竖分；
     * - `subject`：仅主体 bbox 且横↔竖跨画幅；
     * - `169_single_tiny_split` / `single`：同比例下单人，小脸可分上下条 vs 普通人脸跟拍；
     * - `blur_fit`：0 脸、多人非 169 特判、或其它落入默认。
     * 落位到具体每帧的绘制层，见 {@link FJVideoElement#_computeDrawTimelineLayersNorm01} 的 `role` 列表。
     *
     * @returns {{ sameAspect: boolean, profile: string, segmentsMerged: { faceData: object, trackId?: number }[] | null, trackSignature: string }}
     */
    _resolveFrameComposition(inputAr, outputAr, sourceTime) {
        if (FJFaceUtil.approxEqualAspectRatio(inputAr, outputAr)) {
            return { sameAspect: true, profile: 'same', segmentsMerged: null, trackSignature: '' };
        }
        const segmentsMerged = FJFaceUtil.getAllFaceSegmentsAtSourceTime(this._faceDetectionData, sourceTime);
        const profile = FJFaceUtil.resolveFaceRenderProfile(inputAr, outputAr, segmentsMerged);
        return {
            sameAspect: false,
            profile,
            segmentsMerged,
            trackSignature: FJFaceUtil.getSegmentsTrackSignature(segmentsMerged),
        };
    }

    /**
     * 解析目标画布像素尺寸：`outputWidth`+`outputHeight` 优先；否则 `outputRatio` / `aspectRatio`（宽/高）+ `longEdge`（默认 {@link TIMELINE_OUTPUT_MAX_EDGE}）；再否则 `_viewCanvas`；再否则按片源长边推算。
     *
     * @param {{ outputWidth?: number, outputHeight?: number, outputRatio?: number|string|{width:number,height:number}, aspectRatio?: number|string|{width:number,height:number}, longEdge?: number }} [options]
     * @returns {{ width: number, height: number } | null}
     */
    _resolveOutputCanvasSize(options = {}) {
        const ow = Number(options.outputWidth);
        const oh = Number(options.outputHeight);
        if (Number.isFinite(ow) && Number.isFinite(oh) && ow > 0 && oh > 0) {
            return { width: Math.round(ow), height: Math.round(oh) };
        }

        const le = Number(options.longEdge);
        const maxEdge = Number.isFinite(le) && le > 0 ? le : TIMELINE_OUTPUT_MAX_EDGE;

        const ar = parseOutputAspectRatio(options.outputRatio ?? options.aspectRatio);

        let sw = this._originalSize.width;
        let sh = this._originalSize.height;
        if (this._videoPlayer) {
            const s = this._videoPlayer.getSize();
            if (s && s.width > 0 && s.height > 0) {
                sw = s.width;
                sh = s.height;
            }
        }

        if (ar != null && ar > 0) {
            if (ar >= 1) {
                return { width: maxEdge, height: Math.max(1, Math.round(maxEdge / ar)) };
            }
            return { width: Math.max(1, Math.round(maxEdge * ar)), height: maxEdge };
        }

        if (this._viewCanvas && this._viewCanvas.width > 0 && this._viewCanvas.height > 0) {
            return { width: this._viewCanvas.width, height: this._viewCanvas.height };
        }

        if (sw <= 0 || sh <= 0) {
            return null;
        }
        if (sw >= sh) {
            return { width: maxEdge, height: Math.max(1, Math.round((maxEdge * sh) / sw)) };
        }
        return { width: Math.max(1, Math.round((maxEdge * sw) / sh)), height: maxEdge };
    }

    /**
     * 计算某一时间线时刻的「绘制配方」：各层 `role` + 原片/画布上的归一化矩形（0~1）。
     * 与 `_buildTimelineRenderCacheEntries` / `_drawPackedTimelineLayers` 同源；不含交叉淡化。
     *
     * **时间线层 `role` 一览**（与 `FJEditAppUtil` 中 `PRIMARY_LAYER_ROLE_ORDER` 一致，共 10 种；双人/169 分条时同一帧可有多层）：
     * | `role` | 含义（简要） |
     * |--------|-------------|
     * | `same_aspect` | 同画幅，整源 → 整画布 |
     * | `blur_fit` | 全片源 contain 进目标比例（留边，常配模糊底） |
     * | `subject_cover` | 仅主体 bbox：横→竖高拉满 / 竖→横宽拉满，以 bbox 中心裁切 |
     * | `single_cover` | 单人脸，cover 满画布 |
     * | `single_fallback` | 单人构图失败，全源对齐全画布 |
     * | `dual_tiny_fit` | 16:9↔9:16 双小脸，合景一层 contain |
     * | `dual_split_top` / `dual_split_bottom` | 16:9↔9:16 竖分，上/下半区各一人脸 cover |
     * | `dual_split_fallback` | 竖分失败，全源对齐全画布 |
     * | `169_single_full_fit_top` / `169_single_face_bottom` | 16:9 单人分条，上全幅条 fit + 下人脸条 cover |
     *
     * @param {{ bottomCropScale?: number }} [extra] 单人 169 小脸分屏下半取景缩放，缺省用实例值
     * @returns {Array<{ role: string, sourceCrop: object, renderRect: object }>}
     */
    _computeDrawTimelineLayersNorm01(timelineT, fw, fh, cw, ch, dualSplitOptions, extra = {}) {
        const inputAr = fw / fh;
        const outputAr = cw / ch;
        const sourceTime = this._startTime + timelineT;
        const { sameAspect, profile, segmentsMerged } = this._resolveFrameComposition(inputAr, outputAr, sourceTime);

        const pack = (role, sourcePx, destPx) => ({
            role,
            sourceCrop: FJFaceUtil.roundNormRect01(FJFaceUtil.pixelsRectToNorm01(sourcePx, fw, fh), 6),
            renderRect: FJFaceUtil.roundNormRect01(FJFaceUtil.pixelsRectToNorm01(destPx, cw, ch), 6),
        });

        const fullSrcPx = { x: 0, y: 0, width: fw, height: fh };
        const fullDestPx = { x: 0, y: 0, width: cw, height: ch };

        if (sameAspect) {
            return [pack('same_aspect', fullSrcPx, fullDestPx)];
        }

        const segs = segmentsMerged || [];
        const n = segs.length;

        if (profile === 'subject' && n === 1) {
            const cropPx = FJFaceUtil.computeSubjectCrossAspectSourceCropPx(
                segs[0].faceData,
                fw,
                fh,
                inputAr,
                outputAr,
            );
            if (cropPx && cropPx.width > 1 && cropPx.height > 1) {
                return [pack('subject_cover', cropPx, fullDestPx)];
            }
        }

        const dual169 = FJFaceUtil.isSpecial169To916Pair(inputAr, outputAr);
        // }

        if (n === 2 && dual169) {
            const splitPack = FJFaceUtil.computeDualVerticalSplitPersonNormRects(
                segs,
                fw,
                fh,
                cw,
                ch,
                dualSplitOptions,
            );
            if (!splitPack) {
                return [pack('dual_split_fallback', fullSrcPx, fullDestPx)];
            }
            const { halfH, faceDestXRatio, faceDestYRatio, fxt, fyt, fxb, fyb } = splitPack;
            const pt = FJFaceUtil.normRectToPixels(splitPack.normTop, fw, fh);
            const pb = FJFaceUtil.normRectToPixels(splitPack.normBot, fw, fh);
            const clipT = FJFaceUtil.computeCoverClipPixels(
                pt.sx, pt.sy, pt.sw, pt.sh, cw, halfH,
                { x: fxt, y: fyt, destXRatio: faceDestXRatio, destYRatio: faceDestYRatio },
            );
            const clipB = FJFaceUtil.computeCoverClipPixels(
                pb.sx, pb.sy, pb.sw, pb.sh, cw, halfH,
                { x: fxb, y: fyb, destXRatio: faceDestXRatio, destYRatio: faceDestYRatio },
            );
            const layers = [];
            if (clipT) {
                layers.push(
                    pack(
                        'dual_split_top',
                        { x: clipT.csx, y: clipT.csy, width: clipT.cropW, height: clipT.cropH },
                        { x: 0, y: 0, width: cw, height: halfH },
                    ),
                );
            }
            if (clipB) {
                layers.push(
                    pack(
                        'dual_split_bottom',
                        { x: clipB.csx, y: clipB.csy, width: clipB.cropW, height: clipB.cropH },
                        { x: 0, y: halfH, width: cw, height: halfH },
                    ),
                );
            }
            if (layers.length === 0) {
                return [pack('dual_split_fallback', fullSrcPx, fullDestPx)];
            }
            return layers;
        }

        if (profile === '169_single_tiny_split' && n === 1) {
            const tinyCrop = extra.bottomCropScale ?? this._single169TinySplitBottomCropScale;
            const { topH, bottomH } = FJFaceUtil.splitSingle169TinyHalfScreenHeights(cw, ch);
            if (topH >= 1 && bottomH >= 2) {
                const topSrcPx = FJFaceUtil.compute169SingleTinyTopCenterCropSourcePx(fw, fh, cw, topH);
                const topDestPx = { x: 0, y: 0, width: cw, height: topH };
                if (topSrcPx && topSrcPx.sw > 1 && topSrcPx.sh > 1) {
                    try {
                        const normZoom = FJFaceUtil.personCropNormZoomedAboutFaceCenter(
                            segs[0].faceData,
                            cw / bottomH,
                            inputAr,
                            FJFaceUtil.clampSingle169BottomCropScale(tinyCrop),
                        );
                        const { sx, sy, sw, sh } = FJFaceUtil.normRectToPixels(normZoom, fw, fh);
                        const fx = FJFaceUtil.faceCenterX(segs[0].faceData) * fw;
                        const fy = FJFaceUtil.faceCenterY(segs[0].faceData) * fh;
                        const clip = FJFaceUtil.computeCoverClipPixels(
                            sx, sy, sw, sh, cw, bottomH,
                            { x: fx, y: fy, destXRatio: 0.5, destYRatio: 0.5 },
                        );
                        if (clip) {
                            return [
                                pack(
                                    '169_single_full_fit_top',
                                    {
                                        x: topSrcPx.sx,
                                        y: topSrcPx.sy,
                                        width: topSrcPx.sw,
                                        height: topSrcPx.sh,
                                    },
                                    topDestPx,
                                ),
                                pack(
                                    '169_single_face_bottom',
                                    { x: clip.csx, y: clip.csy, width: clip.cropW, height: clip.cropH },
                                    { x: 0, y: topH, width: cw, height: bottomH },
                                ),
                            ];
                        }
                    } catch {
                        /* fall through to single_cover */
                    }
                }
            }
        }

        if ((profile === 'single' || profile === '169_single_tiny_split') && n === 1) {
            try {
                const norm = getPersonCropRectFromFaceData(segs[0].faceData, {
                    aspectRatio: outputAr,
                    sourceAspectRatio: inputAr,
                });
                const { sx, sy, sw, sh } = FJFaceUtil.normRectToPixels(norm, fw, fh);
                const clip = FJFaceUtil.computeCoverClipPixels(sx, sy, sw, sh, cw, ch);
                if (!clip) {
                    return [pack('single_fallback', fullSrcPx, fullDestPx)];
                }
                return [
                    pack(
                        'single_cover',
                        { x: clip.csx, y: clip.csy, width: clip.cropW, height: clip.cropH },
                        fullDestPx,
                    ),
                ];
            } catch {
                return [pack('single_fallback', fullSrcPx, fullDestPx)];
            }
        }

        const dr = FJFaceUtil.getDrawRect({ width: fw, height: fh }, { width: cw, height: ch });
        const blurDestPx = { x: dr.dx, y: dr.dy, width: dr.drawW, height: dr.drawH };
        return [pack('blur_fit', fullSrcPx, blurDestPx)];
    }

    /**
     * 导出/编辑用 `segments`：与当前画幅下 `_computeDrawTimelineLayersNorm01` 的 `layers` 一一对应（同条数、同顺序），
     * 不随人脸检测块内 `block.segments` 的人脸条数变化。
     */
    _timelineSegmentsFromNormLayers(layers) {
        if (!Array.isArray(layers) || layers.length === 0) {
            return [];
        }
        return layers.map(L => ({ sourceCrop: { ...L.sourceCrop }, renderRect: { ...L.renderRect } }));
    }

    /** 标记时间线渲染缓存失效并清空（如 clear、裁剪非法、片源尺寸未就绪）。 */
    _invalidateTimelineRenderCache() {
        this._timelineRenderCache.valid = false;
        this._timelineRenderCache.blocks = [];
        this._timelineRenderCache.clipStart = 0;
        this._timelineRenderCache.clipEnd = 0;
    }

    /**
     * 用人脸数据 + 预览导出比例重算 `_timelineRenderCache`（`setFaceDetectionData` / `setPreviewExportRatio` / `setTimeData` 等会触发）。
     */
    _syncTimelineRenderCacheAfterFaceOrTimeChange() {
        const fw = this._originalSize.width;
        const fh = this._originalSize.height;
        if (
            fw <= 0 ||
            fh <= 0 ||
            !Number.isFinite(this._startTime) ||
            !Number.isFinite(this._endTime) ||
            this._endTime < this._startTime ||
            this._endTime - this._startTime <= 1e-9
        ) {
            this._invalidateTimelineRenderCache();
            return;
        }
        const canvasSize = this._resolveOutputCanvasSize({
            aspectRatio: this._previewExportRatio ?? undefined,
            outputRatio: this._previewExportRatio ?? undefined,
            longEdge: TIMELINE_OUTPUT_MAX_EDGE,
        });
        if (!canvasSize) {
            this._invalidateTimelineRenderCache();
            return;
        }
        const { width: cw, height: ch } = canvasSize;
        const bottomCropTL = this._single169TinySplitBottomCropScale;
        const dual = this._timelinePreviewDualSplitOptions ?? {};
        this._rebuildTimelineRenderCacheLocked(cw, ch, fw, fh, dual, bottomCropTL);
    }

    /**
     * 收集裁剪区间内时间点：裁剪端点 + 与人脸块相交后的起止（用于无间隙划分整条时间线）。
     */
    _collectTimelineBreakpoints(clipA, clipB, faceBlocks, eps) {
        const set = new Set();
        set.add(clipA);
        set.add(clipB);
        if (Array.isArray(faceBlocks)) {
            for (const block of faceBlocks) {
                const bs = Number(block?.startTime);
                const be = Number(block?.endTime);
                if (!Number.isFinite(bs) || !Number.isFinite(be)) {
                    continue;
                }
                const t0 = Math.max(bs, clipA);
                const t1 = Math.min(be, clipB);
                if (t1 - t0 > eps) {
                    set.add(t0);
                    set.add(t1);
                }
            }
        }
        return Array.from(set)
            .filter(t => t >= clipA - eps && t <= clipB + eps)
            .sort((a, b) => a - b);
    }

    /**
     * 构造 **[_startTime, _endTime]** 完整划分：每段 `layers` 与 `_computeDrawTimelineLayersNorm01` 在子区间中点一致，即 `drawVideoFrameAtTimeToCanvas` 在该段内应采样的绘制数据（人脸数据可为空，整段仅一块）。
     * 每段 `profile` 为构图分支：`same` 表示同画幅，否则为 `FJFaceUtil.resolveFaceRenderProfile` 返回值；**具体画面几何** 以同段 `layers[].role` 为准。
     * 每段 `segments` 与同段 `layers` **条数与顺序一致**（由当前输出画幅下构图决定），不依赖人脸时间块内 `segments` 条数。
     *
     * @param {number} cw
     * @param {number} ch
     * @param {number} fw
     * @param {number} fh
     * @param {Record<string, unknown>} dualSplitOptions
     * @param {number} bottomCropTL
     * @returns {Array<{ startTime: number, endTime: number, layers: object[], segments: object[], profile: string, trackSignature: string }>}
     */
    _buildTimelineRenderCacheEntries(cw, ch, fw, fh, dualSplitOptions, bottomCropTL) {
        const out = [];
        const clipA = this._startTime;
        const clipB = this._endTime;
        const eps = 1e-9;
        const faceBlocks = this._faceDetectionData;
        const inputAr = fw / fh;
        const outputAr = cw / ch;

        // 相邻时间点之间为一子区间；整个人脸/无脸区间并起来铺满 [clipA, clipB]
        const points = this._collectTimelineBreakpoints(clipA, clipB, faceBlocks, eps);
        for (let i = 0; i < points.length - 1; i++) {
            const segStart = points[i];
            const segEnd = points[i + 1];
            if (segEnd - segStart <= eps) {
                continue;
            }
            // 用子区间中点做一次构图采样，整段共用该组 layers（与导出时间线块一致）
            const sourceMid = (segStart + segEnd) / 2;
            const timelineT = sourceMid - this._startTime;
            const { sameAspect, profile, trackSignature } = this._resolveFrameComposition(inputAr, outputAr, sourceMid);
            const layersRaw = this._computeDrawTimelineLayersNorm01(timelineT, fw, fh, cw, ch, dualSplitOptions, {
                bottomCropScale: bottomCropTL,
            });
            if (layersRaw.length === 0) {
                continue;
            }
            const layers = layersRaw.map(L => ({
                role: L.role,
                sourceCrop: { ...L.sourceCrop },
                renderRect: { ...L.renderRect },
            }));
            const segments = this._timelineSegmentsFromNormLayers(layersRaw);
            out.push({
                startTime: segStart,
                endTime: segEnd,
                layers,
                segments,
                profile: sameAspect ? 'same' : profile,
                trackSignature,
            });
        }
        return out;
    }

    /**
     * 用给定输出尺寸与片源尺寸锁定重建整段缓存（内部由同步/绘制前校验调用）。
     * @param {number} bottomCropTL 单人 169 小脸下半取景缩放（与实例或 options 对齐）
     */
    _rebuildTimelineRenderCacheLocked(cw, ch, fw, fh, dualSplitOptions, bottomCropTL) {
        const blocks = this._buildTimelineRenderCacheEntries(cw, ch, fw, fh, dualSplitOptions, bottomCropTL);
        this._timelineRenderCache = {
            valid: true,
            outputAr: cw / ch,
            canvasW: cw,
            canvasH: ch,
            fw,
            fh,
            bottomCropScale: bottomCropTL,
            clipStart: this._startTime,
            clipEnd: this._endTime,
            blocks,
        };
    }

    /**
     * 当前帧画布与缓存构建时片源尺寸、输出宽高比、下半缩放一致时可走缓存。
     */
    _matchesTimelineRenderCacheForDraw(fw, fh, cw, ch, bottomCropForDraw) {
        const c = this._timelineRenderCache;
        if (!c.valid) {
            return false;
        }
        if (c.fw !== fw || c.fh !== fh) {
            return false;
        }
        if (Math.abs(c.outputAr - cw / ch) > 1e-4) {
            return false;
        }
        if (Math.abs(c.bottomCropScale - bottomCropForDraw) > 1e-5) {
            return false;
        }
        return true;
    }

    /** 绘制尺寸或片源变化时，用当前 `cw,ch` 即时重算缓存（仍与 `_buildTimelineRenderCacheEntries` 同源）。 */
    _ensureTimelineRenderCacheForDraw(cw, ch, fw, fh, bottomCropForDraw) {
        if (this._matchesTimelineRenderCacheForDraw(fw, fh, cw, ch, bottomCropForDraw)) {
            return;
        }
        const dual = this._timelinePreviewDualSplitOptions ?? {};
        this._rebuildTimelineRenderCacheLocked(cw, ch, fw, fh, dual, bottomCropForDraw);
    }

    /**
     * 在连续划分上查找 `sourceTime` 所在段：非末段为半开 [start, end)，末段闭合到 `clipEnd`，避免与下一段在端点重复命中。
     */
    _findCachedTimelineBlockForSourceTime(sourceTime) {
        const list = this._timelineRenderCache.blocks;
        const eps = 1e-9;
        const n = list.length;
        if (n === 0) {
            return null;
        }
        const clipEnd = this._timelineRenderCache.clipEnd;
        for (let i = 0; i < n; i++) {
            const b = list[i];
            const last = i === n - 1;
            if (sourceTime < b.startTime - eps) {
                continue;
            }
            if (last) {
                if (sourceTime <= clipEnd + eps) {
                    return b;
                }
            } else if (sourceTime < b.endTime - eps) {
                return b;
            }
        }
        return null;
    }

    /**
     * 绘制用：必须从 `_timelineRenderCache.blocks` 取一段；片源时间先夹到 `[clipStart,clipEnd]` 再查找，仍失败则首/末段（缓存有效时必有段）。
     * @returns {typeof this._timelineRenderCache.blocks[0] | null}
     */
    _getTimelineRenderBlockForDraw(sourceTime) {
        const c = this._timelineRenderCache;
        const list = c.blocks;
        if (list.length === 0) {
            return null;
        }
        let b = this._findCachedTimelineBlockForSourceTime(sourceTime);
        if (b) {
            return b;
        }
        // 略超出裁剪或浮点边界：先夹到 [clipStart, clipEnd] 再匹配
        const clipA = c.clipStart;
        const clipB = c.clipEnd;
        const clamped = Math.min(clipB, Math.max(clipA, sourceTime));
        b = this._findCachedTimelineBlockForSourceTime(clamped);
        if (b) {
            return b;
        }
        // 仍不匹配则用首段/末段，避免绘制空窗
        return sourceTime < clipA ? list[0] : list[list.length - 1];
    }

    /** 深拷贝时间线块，供 `getFaceTimelineRenderRects` 返回，避免外部改动内部缓存。 */
    _cloneTimelineBlocksForApi(blocks) {
        return blocks.map(b => ({
            startTime: b.startTime,
            endTime: b.endTime,
            layers: b.layers.map(L => ({
                role: L.role,
                sourceCrop: { ...L.sourceCrop },
                renderRect: { ...L.renderRect },
            })),
            segments: b.segments.map(s => ({
                sourceCrop: { ...s.sourceCrop },
                renderRect: { ...s.renderRect },
            })),
        }));
    }

    /**
     * `getFaceTimelineRenderRects` 是否可直接使用 `_timelineRenderCache`：
     * `layers` 为 0~1 归一化，与画布绝对像素无关，故只需 **输出宽高比 cw/ch**、片源 fw/fh、下半缩放与人脸竖拼 options 与构建缓存时一致。
     */
    _shouldServeTimelineFromCacheForGetFace(options, cw, ch, fw, fh, bottomCropTL) {
        const c = this._timelineRenderCache;
        if (!c.valid || c.fw !== fw || c.fh !== fh) {
            return false;
        }
        const reqAr = cw / ch;
        if (!Number.isFinite(reqAr) || reqAr <= 0 || Math.abs(reqAr - c.outputAr) > 1e-4) {
            return false;
        }
        if (Math.abs(c.bottomCropScale - bottomCropTL) > 1e-5) {
            return false;
        }
        if (options.dualSplitOptions != null && options.dualSplitOptions !== this._timelinePreviewDualSplitOptions) {
            return false;
        }
        return true;
    }

    /**
     * 按单层归一化矩形从片源裁切并画到画布目标区（0~1 已换算为像素）。
     * 用于分屏上下半、单人 cover、169 分屏等多层叠加中的普通 drawImage 路径。
     */
    _drawNormTimelineLayerDrawImage(ctx, frame, layer, fw, fh, cw, ch) {
        const sc = layer.sourceCrop;
        const rd = layer.renderRect;
        const sx = sc.x * fw;
        const sy = sc.y * fh;
        const sw = Math.max(1, sc.width * fw);
        const sh = Math.max(1, sc.height * fh);
        const dx = rd.x * cw;
        const dy = rd.y * ch;
        const dw = Math.max(1, rd.width * cw);
        const dh = Math.max(1, rd.height * ch);
        ctx.drawImage(frame, sx, sy, sw, sh, dx, dy, dw, dh);
    }

    /**
     * 联合取景子画布模糊底 + 前景 fit（几何来自时间线 `dual_tiny_fit` 层）。
     */
    _drawDualTinyFitFromNormLayer(canvas, frame, layer, fw, fh, cw, ch, renderLaneKey) {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        const sc = layer.sourceCrop;
        const rd = layer.renderRect;
        const sx = sc.x * fw;
        const sy = sc.y * fh;
        const sw = sc.width * fw;
        const sh = sc.height * fh;
        const dx = rd.x * cw;
        const dy = rd.y * ch;
        const dw = rd.width * cw;
        const dh = rd.height * ch;
        if (sw <= 1 || sh <= 1 || dw <= 1 || dh <= 1) {
            return;
        }
        let cropCanvas = null;
        try {
            cropCanvas = FJPoolStore.getCanvas('2d');
            const iw = Math.max(1, Math.floor(sw));
            const ih = Math.max(1, Math.floor(sh));
            cropCanvas.width = iw;
            cropCanvas.height = ih;
            cropCanvas.getContext('2d').drawImage(frame, sx, sy, sw, sh, 0, 0, iw, ih);
            this._drawBackgroundBlurToView(canvas, cropCanvas, renderLaneKey);
            ctx.save();
            ctx.translate(dx, dy);
            FJFaceUtil.drawPersonCropFitCanvas(ctx, cropCanvas, { x: 0, y: 0, width: 1, height: 1 }, iw, ih, dw, dh);
            ctx.restore();
        } finally {
            if (cropCanvas) {
                FJPoolStore.releaseCanvas(cropCanvas);
            }
        }
    }

    /** 按 `layers`（时间线缓存）顺序画满画布：`role` 决定 blur / 等比 / 双小脸 / 多层 drawImage。 */
    _drawPackedTimelineLayers(canvas, ctx, frame, fw, fh, cw, ch, layers, renderLaneKey) {
        if (!layers?.length) {
            return;
        }
        const r0 = layers[0].role;
        // 以下分支与 _computeDrawTimelineLayersNorm01 产出的 role 一一对应
        if (r0 === 'blur_fit') {
            this._drawFitVideoWithBackground(canvas, frame, renderLaneKey);
            return;
        }
        if (layers.length === 1 && r0 === 'same_aspect') {
            this._drawDirectSameAspect(ctx, frame, fw, fh, cw, ch);
            return;
        }
        if (layers.length === 1 && r0 === 'dual_tiny_fit') {
            this._drawDualTinyFitFromNormLayer(canvas, frame, layers[0], fw, fh, cw, ch, renderLaneKey);
            return;
        }
        // 多层：按顺序叠加（如 169 上 crop 铺满 + 下 cover、双人上下分屏等）
        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];
            if (layer.role === 'dual_tiny_fit') {
                this._drawDualTinyFitFromNormLayer(canvas, frame, layer, fw, fh, cw, ch, renderLaneKey);
            } else {
                this._drawNormTimelineLayerDrawImage(ctx, frame, layer, fw, fh, cw, ch);
            }
        }
    }

    /**
     * 返回 **[_startTime, _endTime]** 无间隙时间划分（与人脸有无无关）：每段的 `layers` / `profile` 与 `drawVideoFrameAtTimeToCanvas` 使用的 `_timelineRenderCache` 一致（子区间中点采样）。
     *
     * - `startTime` / `endTime`：该片源绝对秒区间端点（相邻段首尾相接；划分节点 = 裁剪端点 ∪ 与人脸块相交区间的端点）。
     * - `layers`：该段内应绘制的全部视频层（与 `_computeDrawTimelineLayersNorm01` 一致）。
     * - `segments`：与 `layers` 一一对应（`sourceCrop` / `renderRect` 拷贝），条数由当前画幅构图决定；无独立人脸块对齐逻辑。
     *
     * @param {{ outputWidth?: number, outputHeight?: number, outputRatio?: number|string|{width:number,height:number}, aspectRatio?: number|string|{width:number,height:number}, longEdge?: number, dualSplitOptions?: Record<string, unknown>, bottomCropScale?: number }} [options] `bottomCropScale`：同 `setSingle169TinySplitOptions`，仅单人 169 小脸分屏
     * @returns {Array<{ startTime: number, endTime: number, layers: Array<{ role: string, sourceCrop: object, renderRect: object }>, segments: Array<{ sourceCrop: object, renderRect: object }> }>}
     *
     * **缓存**：当 `options` 解析出的输出 **宽高比**、片源尺寸、`bottomCropScale`、竖拼 options 与 `_timelineRenderCache` 构建条件一致时，**直接** `clone(_timelineRenderCache.blocks)`，与 `drawVideoFrameAtTimeToCanvas` 同源；否则临时 `_buildTimelineRenderCacheEntries`（不写回缓存）。
     */
    getFaceTimelineRenderRects(options = {}) {
        const canvasSize = this._resolveOutputCanvasSize(options);
        if (!canvasSize) {
            return [];
        }
        const { width: cw, height: ch } = canvasSize;
        const fw = this._originalSize.width;
        const fh = this._originalSize.height;
        if (fw <= 0 || fh <= 0) {
            return [];
        }
        if (
            !Number.isFinite(this._startTime) ||
            !Number.isFinite(this._endTime) ||
            this._endTime < this._startTime ||
            this._endTime - this._startTime <= 1e-9
        ) {
            return [];
        }

        const bottomCropTL =
            options.bottomCropScale != null && Number.isFinite(Number(options.bottomCropScale))
                ? FJFaceUtil.clampSingle169BottomCropScale(options.bottomCropScale)
                : this._single169TinySplitBottomCropScale;
        const dualSplitOptions = options.dualSplitOptions ?? {};

        if (this._shouldServeTimelineFromCacheForGetFace(options, cw, ch, fw, fh, bottomCropTL)) {
            return this._cloneTimelineBlocksForApi(this._timelineRenderCache.blocks);
        }

        const built = this._buildTimelineRenderCacheEntries(cw, ch, fw, fh, dualSplitOptions, bottomCropTL);
        return this._cloneTimelineBlocksForApi(built);
    }

    /**
     * 构图或人脸轨迹变化时抓上一帧到 snapshot，并设定墙钟或时间线转场起点。
     *
     * @param {{ role: string, sourceCrop?: object }[]} [timelineLayers] 当前时刻缓存段 `layers`，用于单人 `single_cover` 裁切相近时跳过叠化。
     */
    // ── 辅助：渲染车道 ──────────────────────────────────────────────────────────

    /**
     * 根据 `transitionClock` 选取对应渲染车道。
     * @param {'wall'|'timeline'} transitionClock
     * @returns {{ lane: object, renderLaneKey: 'preview'|'export' }}
     */
    _pickRenderLane(transitionClock) {
        const renderLaneKey = transitionClock === 'timeline' ? 'export' : 'preview';
        const lane = renderLaneKey === 'export' ? this._renderLaneExport : this._renderLanePreview;
        return { lane, renderLaneKey };
    }

    /**
     * 解析底部裁切比例，回退到实例默认值。
     * @param {number|undefined} optValue
     * @returns {number}
     */
    _resolveBottomCropScale(optValue) {
        return optValue != null && Number.isFinite(Number(optValue))
            ? FJFaceUtil.clampSingle169BottomCropScale(optValue)
            : this._single169TinySplitBottomCropScale;
    }

    /**
     * 从缓存段（或宽高比兜底）解析当前构图模式。
     * @param {object|null} cacheBlock
     * @param {number} inputAr
     * @param {number} outputAr
     * @returns {{ sameAspect: boolean, profile: string, trackSignature: string }}
     */
    _resolveCompositionProfile(cacheBlock, inputAr, outputAr) {
        if (cacheBlock) {
            const { profile, trackSignature } = cacheBlock;
            return { sameAspect: profile === 'same', profile, trackSignature };
        }
        // 缓存无任何段（如裁剪长度为 0）：仅用宽高比决定等比或模糊 letterbox
        const sameAspect = FJFaceUtil.approxEqualAspectRatio(inputAr, outputAr);
        return { sameAspect, profile: sameAspect ? 'same' : 'blur_fit', trackSignature: '' };
    }

    /**
     * 画布尺寸变化时重置转场状态，防止旧 snapshot 尺寸不匹配。
     * @param {object} lane
     * @param {number} cw
     * @param {number} ch
     */
    _resetLaneOnResize(lane, cw, ch) {
        if (lane.lastW === cw && lane.lastH === ch) return;
        lane.lastW = cw;
        lane.lastH = ch;
        lane.lastProfile = null;
        lane.lastTrackSignature = null;
        lane.lastSingleCoverSourceCrop = null;
        lane.wallStartMs = 0;
        lane.timelineStartSec = null;
    }

    /**
     * 计算当前帧的转场进度。
     * @param {object} lane
     * @param {'wall'|'timeline'} transitionClock
     * @param {number} timelineT 时间线秒
     * @returns {{ useCompose: boolean, elapsed: number }}
     */
    _computeTransitionProgress(lane, transitionClock, timelineT) {
        let elapsed = 0;
        if (transitionClock === 'timeline' && lane.timelineStartSec != null) {
            elapsed = (timelineT - lane.timelineStartSec) * 1000;
        } else if (transitionClock === 'wall' && lane.wallStartMs > 0) {
            elapsed = performance.now() - lane.wallStartMs;
        }

        const transitionArmed = transitionClock === 'timeline' ? lane.timelineStartSec != null : lane.wallStartMs > 0;
        if (!transitionArmed) return { useCompose: false, elapsed: 0 };

        if (elapsed >= PREVIEW_MODE_TRANSITION_MS) {
            lane.wallStartMs = 0;
            lane.timelineStartSec = null;
            return { useCompose: false, elapsed };
        }
        return { useCompose: elapsed >= 0, elapsed };
    }

    /**
     * 将已绘制完毕的画布暂存到 `lane.lastOut`，供下一帧转场使用。
     * @param {object} lane
     * @param {HTMLCanvasElement} canvas
     * @param {number} cw
     * @param {number} ch
     */
    _stashLastOutput(lane, canvas, cw, ch) {
        const stash = this._ensureLaneCanvas(lane, 'lastOut', cw, ch);
        try {
            stash.getContext('2d').drawImage(canvas, 0, 0);
        } catch {
            /* ignore */
        }
    }

    // ── 辅助：构图切换交叉淡化 ────────────────────────────────────────────────

    /**
     * 当构图 profile 或人脸轨迹发生有意义变化时，将当前 lastOut 快照到 lane.snapshot 并启动转场计时。
     *
     * @param {object} lane
     * @param {{ cw: number, ch: number, timelineT: number, renderLaneKey: 'preview'|'export' }} ctx
     * @param {{ sameAspect: boolean, profile: string, trackSignature: string, prevProfile: string|null, prevTrackSig: string|null, layers: Array|null }} comp
     */
    _armCrossfadeIfCompositionChanged(lane, ctx, comp) {
        const { cw, ch, timelineT, renderLaneKey } = ctx;
        const { sameAspect, profile, trackSignature, prevProfile, prevTrackSig, layers } = comp;

        const trackOnlyChange =
            !sameAspect && prevProfile !== null && profile === prevProfile && trackSignature !== prevTrackSig;
        if (prevProfile === null || (profile === prevProfile && !trackOnlyChange)) {
            return;
        }
        // blur_fit 均为整源 contain + 模糊底，前后 profile 未变不因 trackSignature 抖动叠化
        if (prevProfile === 'blur_fit' && profile === 'blur_fit') {
            return;
        }
        // single_cover：sourceCrop 与上一帧整体接近时不叠化，避免仅靠轨迹签名抖动触发无意义渐变
        const curScCrop = extractSingleCoverSourceCrop01(layers);
        const prevScCrop = lane.lastSingleCoverSourceCrop;
        if (
            curScCrop &&
            prevScCrop &&
            normRectsAllComponentsCloseEnough(curScCrop, prevScCrop, SINGLE_COVER_NO_TRANSITION_RECT_MAX_DELTA)
        ) {
            return;
        }

        const lastOut = lane.lastOut;
        const snap = this._ensureLaneCanvas(lane, 'snapshot', cw, ch);
        if (lastOut && lastOut.width === cw && lastOut.height === ch) {
            try {
                snap.getContext('2d').drawImage(lastOut, 0, 0);
                if (renderLaneKey === 'preview') {
                    lane.wallStartMs = performance.now();
                    lane.timelineStartSec = null;
                } else {
                    lane.timelineStartSec = timelineT;
                    lane.wallStartMs = 0;
                }
            } catch {
                lane.wallStartMs = 0;
                lane.timelineStartSec = null;
            }
        } else {
            lane.wallStartMs = 0;
            lane.timelineStartSec = null;
        }
    }

    /**
     * 按时间线时间将视频帧绘制到 canvas。
     *
     * 流程：仅使用 `_timelineRenderCache` 中当前时刻对应段的 `layers` / `profile`
     * （与 `getFaceTimelineRenderRects` 同源）→（可选）交叉淡化 → 写入画布并缓存 lastOut。
     * 缓存无任何段时仅黑底 + 等比或 blur 兜底。
     *
     * @param {HTMLCanvasElement} canvas
     * @param {CanvasImageSource} frame
     * @param {number} [time] 时间线秒（相对裁剪起点），与导出器一致
     * @param {{ transitionClock?: 'wall'|'timeline', bottomCropScale?: number }} [options]
     */
    drawVideoFrameAtTimeToCanvas(canvas, frame, time, options = {}) {
        const transitionClock = options.transitionClock ?? 'wall';

        // 1. 选取渲染车道（export / preview 互不干扰）
        const { lane, renderLaneKey } = this._pickRenderLane(transitionClock);

        // 2. 尺寸校验
        const cw = canvas.width;
        const ch = canvas.height;
        const { w: fw, h: fh } = FJFaceUtil.getFrameBitmapSize(frame);
        if (fw <= 0 || fh <= 0 || cw <= 0 || ch <= 0) return;

        // 3. 宽高比 & 时间
        const inputAr = fw / fh;
        const outputAr = cw / ch;
        const timelineT = Number.isFinite(time) ? time : 0;
        const sourceTime = this._startTime + timelineT;

        // 4. 确保构图缓存，获取当前帧所在缓存段
        this._ensureTimelineRenderCacheForDraw(cw, ch, fw, fh, this._resolveBottomCropScale(options.bottomCropScale));
        const cacheBlock = this._getTimelineRenderBlockForDraw(sourceTime);

        // 5. 解析构图 profile（读取 prevProfile 必须在 resize reset 之前）
        const prevProfile = lane.lastProfile;
        const prevTrackSig = lane.lastTrackSignature;
        const { sameAspect, profile, trackSignature } = this._resolveCompositionProfile(cacheBlock, inputAr, outputAr);

        // 6. 画布尺寸变化时重置转场状态
        this._resetLaneOnResize(lane, cw, ch);

        // 7. 构图切换时触发交叉淡化（在绘制之前抢占 snapshot）
        this._armCrossfadeIfCompositionChanged(
            lane,
            { cw, ch, timelineT, renderLaneKey },
            { sameAspect, profile, trackSignature, prevProfile, prevTrackSig, layers: cacheBlock?.layers ?? null },
        );

        // 8. 计算转场进度
        const { useCompose, elapsed } = this._computeTransitionProgress(lane, transitionClock, timelineT);

        // 9. 绘制画面到目标画布（转场中途绘到 compose 离屏，否则直接绘到 canvas）
        const targetCanvas = useCompose ? this._ensureLaneCanvas(lane, 'compose', cw, ch) : canvas;
        const ctx = targetCanvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, cw, ch);
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, cw, ch);

        if (sameAspect) {
            this._drawDirectSameAspect(ctx, frame, fw, fh, cw, ch);
        } else if (cacheBlock) {
            this._drawPackedTimelineLayers(targetCanvas, ctx, frame, fw, fh, cw, ch, cacheBlock.layers, renderLaneKey);
        } else {
            this._drawFitVideoWithBackground(targetCanvas, frame, renderLaneKey);
        }

        // 10. 叠化合成（将 snapshot + compose 混合写入最终 canvas）
        if (useCompose) {
            this._compositeModeTransitionCrossfade(canvas, cw, ch, elapsed, lane);
        }

        // 11. 暂存本帧输出，更新车道状态
        this._stashLastOutput(lane, canvas, cw, ch);
        lane.lastProfile = profile;
        lane.lastTrackSignature = trackSignature;
        const sc01 = extractSingleCoverSourceCrop01(cacheBlock?.layers ?? null);
        lane.lastSingleCoverSourceCrop = sc01 ? { ...sc01 } : null;
    }
}

export default FJVideoElement;
